import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBookmark,
  faBullseye,
  faChartSimple,
  faCheck,
  faChevronRight,
  faEllipsis,
  faForward,
  faLightbulb,
  faList,
  faPlay,
  faRotate,
  faStar,
} from '@fortawesome/free-solid-svg-icons';

const REVIEW_STATES = Object.freeze({
  KEEP: 'KEEP',
  SAVE: 'SAVE',
  SKIP: 'SKIP',
  HUMAN_REVIEW: 'HUMAN_REVIEW',
});

const stateCopy = {
  [REVIEW_STATES.SAVE]: {
    lead: '值得存下来。',
    accent: '以后真会用到',
    reasons: [
      { icon: faList, label: '工具对比完整' },
      { icon: faBullseye, label: '有明确选择标准' },
      { icon: faLightbulb, label: '适合决策时回看' },
    ],
    ratios: { keep: 16, skip: 8, save: 76 },
    summary: 'Jev 已标记 3 个关键片段，方便下次直达',
    detail: '收藏后自动整理到「AI 工具」',
    primary: '收藏视频',
    secondary: '只记重点',
    primaryIcon: faBookmark,
    secondaryIcon: faList,
  },
  [REVIEW_STATES.SKIP]: {
    lead: '可以划走了。',
    accent: '后面没有新东西',
    reasons: [
      { icon: faChartSimple, label: '核心结论已讲完' },
      { icon: faBullseye, label: '后续内容重复' },
      { icon: faLightbulb, label: '没有实测数据' },
    ],
    ratios: { keep: 12, skip: 81, save: 7 },
    summary: 'Jev 已替你看完：不值得再花 2 分钟',
    detail: '以后自动跳过这类内容',
    primary: '跳过，下一条',
    secondary: '继续看',
    primaryIcon: faForward,
    secondaryIcon: faPlay,
  },
  [REVIEW_STATES.KEEP]: {
    lead: '先别划走。',
    accent: '真正的差别在后面',
    reasons: [
      { icon: faChartSimple, label: '不是工具排名' },
      { icon: faBullseye, label: '看任务匹配' },
      { icon: faLightbulb, label: '复杂项目差距最大' },
    ],
    ratios: { keep: 72, skip: 18, save: 10 },
    summary: '接下来 2 分钟，Jev 帮你盯住关键结论',
    detail: '这类视频以后直接告诉我结论',
    primary: '继续看',
    secondary: '换一个',
    primaryIcon: faPlay,
    secondaryIcon: faRotate,
  },
  [REVIEW_STATES.HUMAN_REVIEW]: {
    lead: '先交给你判断。',
    accent: 'Jev 还不能确定',
    reasons: [
      { icon: faChartSimple, label: '信号出现冲突' },
      { icon: faBullseye, label: '需要结合你的任务' },
      { icon: faLightbulb, label: '评论缺少实测证据' },
    ],
    ratios: { keep: 34, skip: 20, save: 46 },
    summary: '这条内容值得你亲自看一眼，再决定下一步',
    detail: 'Jev 会记住你的最终选择',
    primary: '继续看',
    secondary: '换一个',
    primaryIcon: faPlay,
    secondaryIcon: faRotate,
  },
};

const waitingCopy = {
  lead: 'Jev 正在看。',
  accent: '先别急着划走',
  reasons: [
    { icon: faChartSimple, label: '读取视频信号' },
    { icon: faBullseye, label: '比较任务匹配' },
    { icon: faLightbulb, label: '检查重复风险' },
  ],
  ratios: { keep: 0, skip: 0, save: 0 },
  summary: '正在读取评论区，给你一个可执行的下一步',
  detail: 'OBSERVE → COMMENTS → DECIDE',
  primary: '正在判断',
  secondary: '稍等片刻',
  primaryIcon: faRotate,
  secondaryIcon: faChevronRight,
};

function getCopy(decision, decisionReady, isThinking) {
  if (isThinking) return waitingCopy;
  if (!decisionReady) return waitingCopy;
  return stateCopy[decision?.action] || stateCopy[REVIEW_STATES.HUMAN_REVIEW];
}

function getRatios(decision, copy) {
  const probabilities = decision?.probabilities;
  if (!probabilities) return copy.ratios;
  return {
    keep: Math.round(Number(probabilities.KEEP || 0) * 100),
    skip: Math.round(Number(probabilities.SKIP || 0) * 100),
    save: Math.round(Number(probabilities.SAVE || 0) * 100),
  };
}

function JevMark() {
  return <div className="jev-review-mark" aria-hidden="true"><span /><i /></div>;
}

function Ratio({ label, value, active }) {
  return <div className={`jev-review-ratio ${active ? 'is-active' : ''}`}>
    <span>{label}</span>
    <strong>{value}%</strong>
  </div>;
}

export default function JevReviewPhone({
  video,
  decision,
  decisionReady = true,
  isThinking = false,
  apiStatus = 'fixture',
  variant = 'phone',
  onComments,
  onPrimaryAction,
  onSecondaryAction,
}) {
  const copy = getCopy(decision, decisionReady, isThinking);
  const ratios = getRatios(decision, copy);
  const state = isThinking ? 'thinking' : decisionReady ? (decision?.action || REVIEW_STATES.HUMAN_REVIEW).toLowerCase() : 'waiting';
  const status = isThinking ? '正在读取评论' : apiStatus === 'error' ? '请求失败' : apiStatus === 'cached' ? '已缓存' : apiStatus === 'live' ? '实时判断' : '等待判断';
  const sourceName = video?.username || '@jev_user';
  const commentsCount = video?.comments ?? '—';

  return (
    <div className={`jev-review-screen jev-review-screen--${variant} jev-review-screen--${state}`} aria-label="Jev 审核屏幕">
      <div className="jev-review-glass" />
      <div className="jev-review-statusbar"><span>9:41</span><span className="jev-review-island" /><span>◔ ◔ ▰</span></div>

      <header className="jev-review-header">
        <div className="jev-review-brand"><JevMark /><strong>Jev 看完了</strong></div>
        <div className="jev-review-header-actions"><span className="jev-review-status">{status}</span><button type="button" aria-label="更多操作"><FontAwesomeIcon icon={faEllipsis} /></button></div>
      </header>

      <main className="jev-review-content">
        <div className="jev-review-headline" aria-live="polite">
          <span>{copy.lead}</span>
          <em>{copy.accent}</em>
        </div>

        <section className="jev-review-reasons">
          <div className="jev-review-section-label">为什么</div>
          <div className="jev-review-reason-grid">
            {copy.reasons.map((reason) => <div className="jev-review-reason" key={reason.label}>
              <FontAwesomeIcon icon={reason.icon} />
              <span>{reason.label}</span>
            </div>)}
          </div>
        </section>

        <section className="jev-review-outcomes" aria-label="用户选择比例">
          <Ratio label="继续看" value={ratios.keep} active={state === 'keep'} />
          <Ratio label="跳过" value={ratios.skip} active={state === 'skip'} />
          <Ratio label="收藏" value={ratios.save} active={state === 'save'} />
        </section>

        <div className="jev-review-summary"><span /><strong>{copy.summary}</strong></div>

        <section className="jev-review-detail">
          <div className="jev-review-detail-icon"><FontAwesomeIcon icon={state === 'save' ? faList : state === 'skip' ? faForward : faStar} /></div>
          <div>
            <strong>{state === 'save' ? 'Jev 已标记 3 个关键片段，方便下次直达' : copy.detail}</strong>
            {state === 'save' && <div className="jev-review-segments"><span>00:42&nbsp;&nbsp;工具定位对比</span><span>03:15&nbsp;&nbsp;适用场景与人群分析</span><span>06:28&nbsp;&nbsp;最终推荐与总结</span></div>}
            {state !== 'save' && <small>{sourceName} · 评论 {commentsCount} · {decision?.latency ? `${decision.latency} ms` : '本地回放'}</small>}
          </div>
        </section>

        <div className="jev-review-actions">
          <button type="button" className="jev-review-primary" onClick={onPrimaryAction} disabled={!decisionReady || isThinking}>
            <FontAwesomeIcon icon={copy.primaryIcon} spin={isThinking} />
            <span>{copy.primary}</span>
          </button>
          <button type="button" className="jev-review-secondary" onClick={onSecondaryAction || onComments} disabled={isThinking && state === 'thinking'}>
            <FontAwesomeIcon icon={copy.secondaryIcon} />
            <span>{copy.secondary}</span>
          </button>
        </div>

        <button type="button" className="jev-review-source" onClick={onComments}>
          <span><FontAwesomeIcon icon={faCheck} /></span>
          <strong>{state === 'skip' ? '以后自动跳过这类内容' : state === 'save' ? '收藏后自动整理到「AI 工具」' : '这类视频以后直接告诉我结论'}</strong>
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </main>
    </div>
  );
}
